import { PostMessageOption, SystemParam } from '../../types';
import {
  initDefaultBridge,
  postJsMessage,
  registerSpecificNotificationHandler,
  unregisterSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { NativeAPIResponseMessage } from '../../utils/type';
import { ERROR_CODE, genError, isAvailable } from '../utils';
import { safeInvokeNoArg } from '../../utils/function';
import { refreshThemeSkinInternalListener } from 'jssdk/theme';
import { EmojiReactionSkinTone } from 'jssdk/theme/types';

// Constants for notification types
const NOTIFICATION_TYPES = {
  TIME_ZONE_CHANGE: 'UnifyNotify_OnTimeZoneChange',
  TIME_FORMAT_CHANGE: 'UnifyNotify_OnTimeFormatChange',
  CLIENT_SETTING_CHANGED: 'UnifyNotify_OnClientSettingChanged',
} as const;

// Default timeout for API calls
const DEFAULT_TIMEOUT = 3;

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.getSystemParam
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getSystemParam(
  options: { preferCache?: boolean; initJSBridge?: boolean } = {}
): Promise<SystemParam> {
  // Extract options with defaults
  const { preferCache = true, initJSBridge = true } = options;

  // Initialize bridge if needed
  if (!('unifySdk' in window) && initJSBridge) {
    initDefaultBridge();
  }
  if (initJSBridge) {
    refreshThemeSkinInternalListener();
  }

  // Check cache if requested
  if (preferCache) {
    const cachedParam = window.unifyConfig?.systemParam;
    if (cachedParam) {
      return cachedParam;
    }

    // Fallback to Android config if available
    if (typeof window.android_config?.getSystemParam === 'function') {
      try {
        return JSON.parse(
          window.android_config.getSystemParam()
        ) as SystemParam;
      } catch (error) {
        console.warn('Failed to parse Android config system param:', error);
      }
    }
  }

  try {
    const response: NativeAPIResponseMessage<SystemParam> = await postJsMessage(
      API.getSystemParam,
      {},
      { timeout: DEFAULT_TIMEOUT }
    );

    if (response && response.returnCode === 0) {
      // Cache the result
      if (window.unifyConfig) {
        window.unifyConfig.systemParam = response.result;
      }
      return response.result;
    } else {
      throw genError({
        errorCode: ERROR_CODE.WEBVIEW_MISSING_SYSTEM_PARAM,
        apiName: API.getSystemParam,
        message: `Failed to get system param: ${response?.returnCode || 'unknown error'}`,
      });
    }
  } catch (error) {
    if (error instanceof Error && 'errorCode' in error) {
      throw error; // Re-throw generated errors
    }
    throw genError({
      errorCode: ERROR_CODE.WEBVIEW_MISSING_SYSTEM_PARAM,
      apiName: API.getSystemParam,
      message: `Network or timeout error: ${error}`,
    });
  }
}

import type {
  TimeZoneInfoChangeCallback,
  EventHandlerState,
  TimeFormatInfo,
  OnTimeFormatChangeCallback,
} from './types';

// Create a reusable event handler factory
function createEventHandlerState<K>(): EventHandlerState<K> {
  return {
    callbacks: [],
    isRegistered: false,
  };
}

function createGenericHandler<TData>(
  notificationType: string,
  eventState: EventHandlerState<(data: TData) => void>,
  updateSystemParam?: (result: TData) => void
) {
  return (message: NativeAPIResponseMessage<TData>) => {
    const { returnCode, result } = message;
    if (returnCode === 0) {
      eventState.callbacks.forEach((fn) => {
        safeInvokeNoArg(() => fn(result));
      });
      updateSystemParam?.(result);
    } else {
      console.warn(`Event handler for ${notificationType} failed:`, message);
    }
  };
}

const timeZoneState = createEventHandlerState<TimeZoneInfoChangeCallback>();
const onTimeZoneInfoChangeHandler = createGenericHandler(
  NOTIFICATION_TYPES.TIME_ZONE_CHANGE,
  timeZoneState,
  (result) => {
    if (window.unifyConfig?.systemParam) {
      window.unifyConfig.systemParam.timeZone = result.timezone;
      window.unifyConfig.systemParam.timeZoneOffset = result.timeZoneOffset;
    }
  }
);

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onTimeZoneChange
 *
 * [PLATFORM]
 * mac        6.1.15+
 * windows    6.1.15+
 * linux      NotSupport
 * ios        6.1.15+
 * android    6.1.15+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onTimeZoneChange(
  onTimezoneInfoChangeFn: TimeZoneInfoChangeCallback
) {
  await isAvailable('platform.onTimeZoneChange');
  timeZoneState.callbacks.push(onTimezoneInfoChangeFn);

  if (!timeZoneState.isRegistered) {
    timeZoneState.isRegistered = true;
    registerSpecificNotificationHandler(
      NOTIFICATION_TYPES.TIME_ZONE_CHANGE,
      onTimeZoneInfoChangeHandler
    );
  }
  return Promise.resolve(); // keep back compatible
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offTimeZoneChange
 *
 * [PLATFORM]
 * mac        6.1.15+
 * windows    6.1.15+
 * linux      NotSupport
 * ios        6.1.15+
 * android    6.1.15+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offTimeZoneChange(
  onTimezoneInfoChangeFn: TimeZoneInfoChangeCallback
) {
  await isAvailable('platform.offTimeZoneChange');
  timeZoneState.callbacks = timeZoneState.callbacks.filter(
    (fn) => fn !== onTimezoneInfoChangeFn
  );
  if (timeZoneState.callbacks.length === 0) {
    unregisterSpecificNotificationHandler(
      NOTIFICATION_TYPES.TIME_ZONE_CHANGE,
      onTimeZoneInfoChangeHandler
    );
    timeZoneState.isRegistered = false;
  }
  return Promise.resolve(); // keep back compatible
}

const timeFormatState = createEventHandlerState<OnTimeFormatChangeCallback>();

// Special handler for time format that passes the entire message
const onTimeFormatChangeHandler = (
  message: NativeAPIResponseMessage<TimeFormatInfo>
) => {
  const { returnCode, result } = message;
  if (returnCode === 0) {
    timeFormatState.callbacks.forEach((fn) => {
      safeInvokeNoArg(() => fn(message));
    });
    if (window.unifyConfig?.systemParam) {
      window.unifyConfig.systemParam.is24TimeFormat = result.is24TimeFormat;
    }
  } else {
    console.warn(
      `Event handler for ${NOTIFICATION_TYPES.TIME_FORMAT_CHANGE} failed:`,
      message
    );
  }
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onTimeFormatChange
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function onTimeFormatChange(
  onTimeFormatChangeFn: OnTimeFormatChangeCallback
) {
  await isAvailable('platform.onTimeFormatChange');
  timeFormatState.callbacks.push(onTimeFormatChangeFn);

  if (!timeFormatState.isRegistered) {
    timeFormatState.isRegistered = true;
    registerSpecificNotificationHandler(
      NOTIFICATION_TYPES.TIME_FORMAT_CHANGE,
      onTimeFormatChangeHandler
    );
  }
  return Promise.resolve();
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offTimeFormatChange
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      NotSupport
 * ios        6.2.0+
 * android    6.2.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function offTimeFormatChange(
  onTimeFormatChangeFn: OnTimeFormatChangeCallback
) {
  await isAvailable('platform.offTimeFormatChange');
  timeFormatState.callbacks = timeFormatState.callbacks.filter(
    (fn) => fn !== onTimeFormatChangeFn
  );
  if (timeFormatState.callbacks.length === 0) {
    unregisterSpecificNotificationHandler(
      NOTIFICATION_TYPES.TIME_FORMAT_CHANGE,
      onTimeFormatChangeHandler
    );
    timeFormatState.isRegistered = false;
  }
  return Promise.resolve();
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.getClientSetting
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getClientSetting<V extends ValueType = 'string'>(
  key: string,
  valueType: V = 'string' as V,
  options?: PostMessageOption
) {
  await isAvailable('platform.getClientSetting');
  return postJsMessage<
    GetClientSetting,
    ClientSetting<ValueTypeToReturnType<V>>
  >(API.getClientSetting, { key, valueType }, options);
}

export type ValueType = 'string' | 'bool' | 'int';
export type ValueTypeToReturnType<T extends ValueType> = T extends 'string'
  ? string
  : T extends 'bool'
    ? boolean
    : T extends 'int'
      ? number
      : never;
export type GetClientSetting = {
  key: string;
  valueType?: ValueType;
};
export type ClientSetting<V> = {
  key: string;
  value: V;
};

export type RawSetting = {
  'accessibility.chatDisplaySize'?: string;
  'theme.emojiReactionSkin'?: string;
  'theme.teamChatSidebarSkin'?: 'light_contrast' | 'dark_contrast';
};

export type Setting = {
  'accessibility.chatDisplaySize'?: number;
  'theme.emojiReactionSkin'?: EmojiReactionSkinTone;
  'theme.teamChatSidebarSkin'?: 'light_contrast' | 'dark_contrast';
};

export type OnRawClientSettingChange = (
  notification: NativeAPIResponseMessage<RawSetting>
) => void;

export type OnClientSettingChange = (
  notification: NativeAPIResponseMessage<Setting>
) => void;

function formatClientSettingData(change: RawSetting): Setting {
  const setting: Setting = {};

  // Type-safe iteration over known keys
  (Object.keys(change) as Array<keyof RawSetting>).forEach((key) => {
    const value = change[key];
    if (value === undefined) return;

    switch (key) {
      case 'accessibility.chatDisplaySize': {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed)) {
          setting[key] = parsed;
        }
        break;
      }
      case 'theme.emojiReactionSkin': {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed)) {
          setting[key] = parsed as EmojiReactionSkinTone;
        }
        break;
      }
      case 'theme.teamChatSidebarSkin': {
        // Type guard to ensure value is valid
        if (value === 'light_contrast' || value === 'dark_contrast') {
          setting[key] = value;
        }
        break;
      }
      default: {
        // TypeScript exhaustiveness check - this should never be reached
        void (key as never);
        break;
      }
    }
  });
  return setting;
}

const settingChangedCBWrapperMap = new WeakMap<
  OnClientSettingChange,
  OnRawClientSettingChange
>();
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.onClientSettingChanged
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onClientSettingChanged(cb: OnClientSettingChange) {
  if (!settingChangedCBWrapperMap.has(cb)) {
    const wrappedCb: OnRawClientSettingChange = (notification) => {
      try {
        if (notification.returnCode === 0) {
          const normalizedNotification: NativeAPIResponseMessage<Setting> = {
            ...notification,
            result: formatClientSettingData(notification.result),
          };
          cb(normalizedNotification);
        } else {
          // Pass through error responses
          cb(notification as NativeAPIResponseMessage<Setting>);
        }
      } catch (error) {
        console.error('Error processing client setting change:', error);
      }
    };
    settingChangedCBWrapperMap.set(cb, wrappedCb);
  }

  const wrappedCb = settingChangedCBWrapperMap.get(cb)!;
  registerSpecificNotificationHandler(
    NOTIFICATION_TYPES.CLIENT_SETTING_CHANGED,
    wrappedCb
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * platform.offClientSettingChanged
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offClientSettingChanged(cb: OnClientSettingChange) {
  const wrappedCb = settingChangedCBWrapperMap.get(cb);
  if (wrappedCb) {
    unregisterSpecificNotificationHandler(
      NOTIFICATION_TYPES.CLIENT_SETTING_CHANGED,
      wrappedCb
    );
    settingChangedCBWrapperMap.delete(cb);
  }
}

export default {
  getSystemParam,
  onTimeZoneChange,
  offTimeZoneChange,
  onTimeFormatChange,
  offTimeFormatChange,
  getClientSetting,
  onClientSettingChanged,
  offClientSettingChanged,
};
